// src/cockpit4/components/DailyExperiencePanel.tsx
// ============================================
// 每日经验沉淀面板（第十四章 Content Protocol · 经验驱动选题）
//
// 主张：「内容主要靠经验和分享才有吸引力，每天沉淀一条经验，
// 而不是每天硬找一个选题」。
// 素材 = XEAOS 这家 AI 公司今天真实的决策/日志/复盘/记忆。
//
// 功能：
//   1. 读取今日经验（ContentStrategyRuntime.get_daily_experience）
//   2. 一键沉淀到知识库（persist_daily_experience → 10_Operations/Daily/经验-{date}.md）
//   3. 选中经验 → 生成 Platform Brief（generate_brief，topic=经验）
// ============================================

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, Lightbulb, Loader2, BookDown, FileText, ChevronRight, Check,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { ContentStrategyQuery, ContentStrategyCommand } from "../../runtime/ContentStrategyRuntime";

interface ExperienceItem {
  text: string;
  score: number;
  source: string;
  kind: "经验" | "教训" | "洞察" | "决策" | "方法" | "案例";
}

const KIND_COLOR: Record<string, string> = {
  经验: "#34D399", 教训: "#F87171", 洞察: "#38BDF8", 决策: "#FBBF24", 方法: "#A78BFA", 案例: "#FB923C",
};

export default function DailyExperiencePanel() {
  const [experiences, setExperiences] = useState<ExperienceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [persisting, setPersisting] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [briefTopic, setBriefTopic] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await RuntimeManager.query(RuntimeName.ContentStrategy, {
        type: ContentStrategyQuery.GetDailyExperience,
      } as any);
      const data: any = resp?.success ? resp.data : resp;
      const list = Array.isArray(data) ? data : data?.experiences ?? [];
      setExperiences(list);
    } catch {
      setExperiences([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const persistDaily = async () => {
    setPersisting(true);
    try {
      const resp = await RuntimeManager.execute(
        RuntimeName.ContentStrategy,
        ContentStrategyCommand.PersistDailyExperience,
        {} as any
      );
      const data: any = resp?.success ? resp.data : resp;
      setFlash(`✅ 已沉淀 ${data?.experiences?.length ?? 0} 条经验 → ${data?.persistedPath || "10_Operations/Daily/经验-*.md"}`);
      setTimeout(() => setFlash(null), 5000);
      load();
    } catch (err: any) {
      setFlash(`❌ 沉淀失败：${err?.message || "未知错误"}`);
      setTimeout(() => setFlash(null), 4000);
    } finally {
      setPersisting(false);
    }
  };

  const generateFromExperience = async (e: ExperienceItem) => {
    setGenerating(e.text);
    try {
      const resp = await RuntimeManager.execute(
        RuntimeName.ContentStrategy,
        ContentStrategyCommand.GenerateBrief,
        { brand: "xeaos", platform: "wechat", goal: "brand", topic: e.text } as any
      );
      const data: any = resp?.success ? resp.data : resp;
      const brief = data?.brief ?? data;
      setBriefTopic(brief?.experience?.topic || e.text);
      setFlash(`✅ 已基于经验生成策略简报（平台：${brief?.platformName || "公众号"}）`);
      setTimeout(() => setFlash(null), 5000);
    } catch (err: any) {
      setFlash(`❌ 生成失败：${err?.message || "未知错误"}`);
      setTimeout(() => setFlash(null), 4000);
    } finally {
      setGenerating(null);
    }
  };

  return (

    <div className="space-y-3">


      {flash && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-[10px] text-amber-300 break-all">
          {flash}
        </motion.div>
      )}

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <FileText size={11} className="text-amber-400" />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
            今日素材 · 来自 XEAOS 真实运营（决策/日志/复盘/记忆）
          </span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-4 text-gray-500">
            <Loader2 size={14} className="animate-spin mr-2" /> 正在扫描今日经验…
          </div>
        ) : experiences.length === 0 ? (
          <div className="py-3 text-center text-[10px] text-gray-600">
            今日暂无经验素材。系统会自动从决策库/日志/复盘/记忆提取，「公开一家 AI 公司是怎么设计出来的」就是天然的内容源。
          </div>
        ) : (
          <div className="space-y-2">
            {experiences.map((e, i) => (
              <div key={i} className="rounded-lg border border-white/[0.06] bg-black/20 p-2.5">
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-mono" style={{ background: `${KIND_COLOR[e.kind]}22`, color: KIND_COLOR[e.kind] }}>
                    {e.kind}
                  </span>
                  <span className="text-[9px] font-mono text-amber-400">评分 {e.score}</span>
                  <span className="ml-auto text-[9px] font-mono text-gray-600 truncate">{e.source.split("/").pop()}</span>
                </div>
                <div className="mt-1.5 text-[11px] text-gray-200 leading-relaxed">{e.text}</div>
                <button onClick={() => generateFromExperience(e)} disabled={generating === e.text}
                  className="mt-2 flex items-center gap-1 px-2 py-1 rounded-md bg-sky-500/10 border border-sky-500/25 text-[9px] text-sky-300 hover:bg-sky-500/20 disabled:opacity-50">
                  {generating === e.text ? <Loader2 size={9} className="animate-spin" /> : <ChevronRight size={9} />}
                  {generating === e.text ? "生成中…" : "基于该经验生成策略简报"}
                </button>
              </div>
            ))}
          </div>
        )}
        {briefTopic && (
          <div className="mt-2 rounded-md border border-sky-500/20 bg-sky-500/[0.05] p-2 text-[10px] text-sky-300">
            <Check size={10} className="inline mr-1" /> 本次简报选题（经验驱动）：{briefTopic}
          </div>
        )}
      </div>

      <div className="text-[9px] text-gray-600 leading-relaxed">
        为什么经验驱动？白皮书第十四章：内容靠经验与分享才有吸引力。每天把「这家 AI 公司今天真实发生了什么」
        沉淀为一条经验，它天然具备：真实细节、第一人称视角、可复用的方法论——远胜于硬找的泛泛选题。
      </div>
    </div>
  );
}
